import * as IBAN from "iban";
import Interpreter from "js-interpreter";
import { format, zonedTimeToUtc } from "date-fns-tz";
import { de } from "date-fns/locale";
import { APP_TIMEZONE } from "@accredo/fe-shared/services/formatService.js";

const not_available = "- - -";

export function cloneDeep(obj, seen = new WeakMap()) {
  // Primitive Werte oder Funktionen direkt zurückgeben
  if (obj === null || typeof obj !== "object") return obj;
  if (typeof obj === "function") return obj;

  // Zirkuläre Referenzen behandeln
  if (seen.has(obj)) return seen.get(obj);

  // Date-Objekt
  if (obj instanceof Date) return new Date(obj.getTime());

  // Array
  if (Array.isArray(obj)) {
    const copy = [];
    seen.set(obj, copy);
    obj.forEach((el, i) => {
      copy[i] = cloneDeep(el, seen);
    });
    return copy;
  }

  // Objekt
  const copy = {};
  seen.set(obj, copy);
  Object.keys(obj).forEach((key) => {
    copy[key] = cloneDeep(obj[key], seen);
  });
  return copy;
}

export const getRWObjectCopy = (obj) => {
  return JSON.parse(JSON.stringify(obj));
};

export const objIsNotEmpty = (obj) => {
  return Object.entries(obj).length > 0;
};

export const compareObjects = (obj1, obj2) => {
  for (const key in obj1) {
    if (obj1.hasOwnProperty(key)) {
      if (!obj2.hasOwnProperty(key) || obj1[key] !== obj2[key]) {
        return false;
      }
    }
  }

  for (const key in obj2) {
    if (obj2.hasOwnProperty(key) && !obj1.hasOwnProperty(key)) {
      return false;
    }
  }

  return true;
};

export const disableControls = (controls) => {
  return controls.map((element) => {
    const updatedElement = {
      ...element,
      disabled: element.disabled || "true",
    };

    if (element.rowControls) {
      updatedElement.rowControls = disableControls(element.rowControls);
    }
    if (element.steps) {
      updatedElement.steps = disableControls(element.steps);
    }
    if (element.children) {
      updatedElement.children = disableControls(element.children);
    }

    return updatedElement;
  });
};

export const flattenItems = (structure) => {
  let result = [];
  const filter = ["accordion", "expandable", "stepper"];
  structure &&
    structure.forEach((item) => {
      if (filter.includes(item.control)) {
        switch (item.control) {
          case "accordion":
          case "expandable":
            result = result.concat(flattenItems(item.children));
            break;
          case "stepper":
            item.steps.forEach((step) => {
              result = result.concat(flattenItems(step.children));
            });
            break;
          default:
        }
      } else result = result.concat(item);
    });
  return result;
};

export const collectAttachmentSources = (structure, excludeFieldName = null) =>
  flattenItems(structure)
    .filter(
      (item) =>
        item.control === "claim_attachments" &&
        item.track_as_source &&
        item.name !== excludeFieldName
    )
    .map((item) => ({
      name: item.name,
      label: item.source_label || item.label || item.name,
    }));

export const initInterpreter = (model, items, exposeErrors = true) => {
  let item;
  let localExposeErrors = exposeErrors;
  let aerrors = {};
  const initFunc = function (interpreter, globalObject) {
    interpreter.setProperty(globalObject, "item", {});
    interpreter.setProperty(globalObject, "model", model || []);
    interpreter.setProperty(globalObject, "items", items || []);
    interpreter.setProperty(globalObject, "aerrors", aerrors);

    const getValue = function (name) {
      if (model) return model[name];
      else return undefined;
    };
    interpreter.setProperty(
      globalObject,
      "getValue",
      interpreter.createNativeFunction(getValue)
    );

    const startsWith = function (name, toSearch, error) {
      let result_boolean = true;
      if (model) {
        if (!Array.isArray(toSearch)) toSearch = [toSearch];
        toSearch.forEach((searchWord) => {
          const value = model[name];
          let single_result_boolean = true;
          if (value) single_result_boolean = value.startsWith(searchWord);
          if (error) error = error.replace("{%var%}", searchWord);
          if (!single_result_boolean && localExposeErrors)
            aerrors[name] = error || "Startet mit " + searchWord;
          result_boolean = result_boolean && single_result_boolean;
        });
      }
      return result_boolean;
    };
    interpreter.setProperty(
      globalObject,
      "startsWith",
      interpreter.createNativeFunction(startsWith)
    );

    const startsNotWith = function (name, toSearch, error) {
      let result_boolean = true;
      if (model) {
        if (!Array.isArray(toSearch)) toSearch = [toSearch];
        toSearch.forEach((searchWord) => {
          const value = model[name];
          let single_result_boolean = true;
          let singleError = null;
          if (value) single_result_boolean = !value.startsWith(searchWord);
          if (error) singleError = error.replace("{%var%}", searchWord);
          if (!single_result_boolean && localExposeErrors)
            aerrors[name] = singleError || "Startet nicht mit " + searchWord;
          result_boolean = result_boolean && single_result_boolean;
        });
      }
      return result_boolean;
    };
    interpreter.setProperty(
      globalObject,
      "startsNotWith",
      interpreter.createNativeFunction(startsNotWith)
    );

    const empty = function (local_item) {
      let value = model[local_item.name];
      if (typeof value === "number") value = value.toString();

      const filter = ["", null, undefined, false];
      return filter.includes(value) || !objIsNotEmpty(value);
    };
    interpreter.setProperty(
      globalObject,
      "empty",
      interpreter.createNativeFunction(empty)
    );

    const isEmpty = function (local_item, error) {
      const result_boolean = empty(local_item);
      if (localExposeErrors && !result_boolean)
        aerrors[local_item.name] = error || "muss leer sein";
      return result_boolean;
    };
    interpreter.setProperty(
      globalObject,
      "isEmpty",
      interpreter.createNativeFunction(isEmpty)
    );

    const isBigger = function (local_item, cmpWith, error) {
      const result_boolean = model[local_item.name] > cmpWith;
      if (localExposeErrors && result_boolean)
        aerrors[local_item.name] = error || "muss kleiner sein.";
      return result_boolean;
    };
    interpreter.setProperty(
      globalObject,
      "isBigger",
      interpreter.createNativeFunction(isBigger)
    );

    const isNotEmpty = function (local_item, error) {
      const result_boolean = !empty(local_item);
      if (localExposeErrors && !result_boolean)
        aerrors[local_item.name] = error || "Pflichtfeld";
      return result_boolean;
    };
    interpreter.setProperty(
      globalObject,
      "isNotEmpty",
      interpreter.createNativeFunction(isNotEmpty)
    );

    const isEmptyOther = function (name, error) {
      const result_boolean = empty(getFieldDefintionByName(name));
      if (localExposeErrors && !result_boolean)
        aerrors[name] = error || "ist nicht leer";
      return result_boolean;
    };
    interpreter.setProperty(
      globalObject,
      "isEmptyOther",
      interpreter.createNativeFunction(isEmptyOther)
    );

    const isNotEmptyOther = function (name, error) {
      const result_boolean = !empty(getFieldDefintionByName(name));
      if (localExposeErrors && !result_boolean)
        aerrors[name] = error || "ist leer";
      return result_boolean;
    };
    interpreter.setProperty(
      globalObject,
      "isNotEmptyOther",
      interpreter.createNativeFunction(isNotEmptyOther)
    );

    const fieldsAreEqual = function (name, name1, error) {
      const result_boolean = model[name] === model[name1];
      if (localExposeErrors && !result_boolean) {
        aerrors[name] = error || "Felder sind ungleich";
        aerrors[name1] = error || "Felder sind ungleich";
      }
      return result_boolean;
    };
    interpreter.setProperty(
      globalObject,
      "fieldsAreEqual",
      interpreter.createNativeFunction(fieldsAreEqual)
    );

    const isEqualOther = function (name, value, error) {
      const result_boolean = model[name] === value;
      if (localExposeErrors && !result_boolean)
        aerrors[name] = error || "Felder sind ungleich";
      return result_boolean;
    };
    interpreter.setProperty(
      globalObject,
      "isEqualOther",
      interpreter.createNativeFunction(isEqualOther)
    );

    const isNotEqualOther = function (name, value, error) {
      const result_boolean = !(model[name] === value);
      if (localExposeErrors && !result_boolean)
        aerrors[name] = error || "Felder sind gleich";
      return result_boolean;
    };
    interpreter.setProperty(
      globalObject,
      "isNotEqualOther",
      interpreter.createNativeFunction(isNotEqualOther)
    );

    const isEmail = function (local_item, error) {
      const modelValue = model[local_item.name] || null;

      let result_boolean = true;

      if (!modelValue) result_boolean = false;
      if (result_boolean) {
        const parts = modelValue.split("@");
        if (modelValue.indexOf("@") === -1) result_boolean = false;

        if (result_boolean) {
          if (parts.length !== 2) result_boolean = false;
          if (parts[1].indexOf(".") === -1) result_boolean = false;
        }

        if (result_boolean) {
          const domainParts = parts[1].split(".");
          if (domainParts.length < 2) result_boolean = false;
          if (domainParts[domainParts.length - 1].length < 2)
            result_boolean = false;
        }
      }

      if (localExposeErrors && !result_boolean)
        aerrors[local_item.name] = error || "ungültige E-Mail Adresse";
      return result_boolean;
    };
    interpreter.setProperty(
      globalObject,
      "isEmail",
      interpreter.createNativeFunction(isEmail)
    );

    const isIHKRegisternummer = function (local_item, error) {
      const modelValue = model[local_item.name] || null;
      const result_boolean = !(
        modelValue === "" ||
        !/^D-[A-Z0-9]{4}-[A-Z0-9]{5}-[A-Z0-9]{2}$/.test(modelValue)
      );
      if (localExposeErrors && !result_boolean)
        aerrors[local_item.name] = error || "IHK Registernummer ist ungültig";
      return result_boolean;
    };
    interpreter.setProperty(
      globalObject,
      "isIHKRegisternummer",
      interpreter.createNativeFunction(isIHKRegisternummer)
    );

    const isIBAN = function (local_item, error) {
      const modelValue = model[local_item.name] || null;
      const result_boolean = modelValue && IBAN.isValid(modelValue);
      if (localExposeErrors && !result_boolean)
        aerrors[local_item.name] = error || "IBAN ist ungültig";
      return result_boolean;
    };
    interpreter.setProperty(
      globalObject,
      "isIBAN",
      interpreter.createNativeFunction(isIBAN)
    );

    /** silly function because we cannot use ORed validation rules,
     * since they have side effects (e.g. in an ORed condition, the failing branch
     * already writes its error message and the error is displayed regardless
     * of the actual validation state of the field
     */
    const isIBANOrEmpty = function (local_item, error) {
      const modelValue = model[local_item.name] || null;
      const result_boolean = !modelValue || IBAN.isValid(modelValue);
      if (localExposeErrors && !result_boolean)
        aerrors[local_item.name] = error || "IBAN ist ungültig";
      return result_boolean;
    };
    interpreter.setProperty(
      globalObject,
      "isIBANOrEmpty",
      interpreter.createNativeFunction(isIBANOrEmpty)
    );

    const isEqual = function (local_item, value, error) {
      const modelValue = model[local_item.name] || null;
      const result_boolean = String(modelValue) === String(value);
      if (localExposeErrors && !result_boolean)
        aerrors[local_item.name] = error || "ungleich";
      return result_boolean;
    };
    interpreter.setProperty(
      globalObject,
      "isEqual",
      interpreter.createNativeFunction(isEqual)
    );

    const isNotEqual = function (local_item, value, error) {
      const modelValue = model[local_item.name] || null;
      const result_boolean = String(modelValue) !== String(value);
      if (localExposeErrors && !result_boolean)
        aerrors[local_item.name] = error || "ungleich";
      return result_boolean;
    };
    interpreter.setProperty(
      globalObject,
      "isNotEqual",
      interpreter.createNativeFunction(isNotEqual)
    );

    const isValid = function (item) {
      if (item.valid) {
        const result_boolean = eval(item.valid);
        return result_boolean;
      }
      return true;
    };
    interpreter.setProperty(
      globalObject,
      "isValid",
      interpreter.createNativeFunction(isValid)
    );

    const isFormValid = function (error) {
      const result_boolean = isSubFormValid(null);
      return result_boolean;
    };
    interpreter.setProperty(
      globalObject,
      "isFormValid",
      interpreter.createNativeFunction(isFormValid)
    );

    const isNotFormValid = function (error) {
      const result_boolean = !isSubFormValid(null);
      return result_boolean;
    };
    interpreter.setProperty(
      globalObject,
      "isNotFormValid",
      interpreter.createNativeFunction(isNotFormValid)
    );

    const isSubFormValid = function (item) {
      let local_items = [];
      if (item) {
        if (item.children) local_items = item.children;
      } else local_items = items;
      let result_boolean = true;
      if (local_items)
        Object.entries(local_items).forEach(([key, local_item]) => {
          localExposeErrors = false;
          let isHidden = false;
          let isDisabled = false;
          if (local_item.hasOwnProperty("hidden"))
            isHidden = eval(local_item.hidden);
          if (local_item.hasOwnProperty("disabled"))
            isDisabled = eval(local_item.disabled);
          localExposeErrors = exposeErrors;
          if (!isHidden && !isDisabled) {
            //if (!isHidden && local_item.hasOwnProperty("valid")) {
            let singleResult = isValid(local_item);
            result_boolean = singleResult && result_boolean;
          }
        });
      if (!result_boolean && item)
        aerrors[item["name"]] = "Unterformular ist ungültig";
      return result_boolean;
    };
    interpreter.setProperty(
      globalObject,
      "isSubFormValid",
      interpreter.createNativeFunction(isSubFormValid)
    );

    const getFieldDefintionByName = function (name) {
      return Object.entries(items).find(
        ([key, item]) => item["name"] === name
      )[1];
    };
    interpreter.setProperty(
      globalObject,
      "getFieldDefintionByName",
      interpreter.createNativeFunction(getFieldDefintionByName)
    );
  };

  //let start = new Date().getTime();
  const myInterpreter = new Interpreter("", initFunc);
  //console.log("initialized", new Date().getTime() - start);
  return myInterpreter;
};

export const evaluate = (expression, model, items, exposeErrors) => {
  try {
    if (!model || typeof model !== "object") {
      throw new Error("Ungültiges Modell übergeben.");
    }
    if (!items || !Array.isArray(items)) {
      throw new Error("Ungültige Items übergeben.");
    }

    const myInterpreter = initInterpreter(model, items, exposeErrors);
    myInterpreter.appendCode(expression);
    myInterpreter.run();
    const result_boolean = myInterpreter.value;

    myInterpreter.appendCode("aerrors");
    myInterpreter.run();
    const result_aerrors = myInterpreter.value;

    return { result: result_boolean, aerrors: result_aerrors };
  } catch (error) {
    console.error(
      "Fehler bei der Auswertung:",
      error.message,
      expression,
      model,
      items
    );
    return { result: false, aerrors: { global: error.message } };
  }
};

export const getEmptyModel = (structure) => {
  let model = {};
  const filter = ["accordion", "stepper"];
  structure.forEach((item) => {
    if (filter.includes(item.control)) {
      switch (item.control) {
        case "accordion":
          Object.assign(model, getEmptyModel(item.children));
          break;
        case "stepper":
          item.steps.forEach((step) => {
            Object.assign(model, getEmptyModel(step.children));
          });
          break;
        default:
      }
    }

    let defaultValue = "";
    switch (item.control) {
      case "select":
      case "autocomplete":
        defaultValue = item.default || "";
        break;
      case "upload":
      case "expandable":
      case "claim_costs_editor":
      case "claim_attachments":
      case "coverage_type_questions":
        defaultValue = item.default || [];
        break;
      case "switch":
        defaultValue = item.default || false;
        break;
      case "datepicker":
      case "radiogroup":
      case "rating":
      case "detailed_radio_group":
        defaultValue = item.default || null;
        break;
      case "checkbox":
        defaultValue = item.default || false;
        break;
      case "password":
      case "input":
      case "textarea":
        defaultValue = item.default || "";
        break;
      case "sliderinput":
        defaultValue = item.default || item.min || 0;
        break;
      case "numberinput":
      case "twolabelslider":
        // null (not "") - these cast to yup.number(), and an empty string
        // fails its type check as NaN even when the field isn't required,
        // while null passes cleanly via .nullable().
        defaultValue = item.default ?? null;
        break;
      default:
    }

    if (item.name) model[item.name] = item.default || defaultValue;
  });
  return model;
};

export const getAPIEndpoint = (action, values) => {
  let endpoint = action.endpoint;
  if (action.eval_endpoint) {
    const evalRes = evaluate(action.endpoint, values, []);
    endpoint = evalRes.result;
  }
  if (endpoint.endsWith("/")) {
    let lastIndex = endpoint.lastIndexOf("/");
    endpoint = endpoint.slice(0, lastIndex);
  }
  return endpoint;
};

export const handleAPICall = async (
  action,
  values,
  apiGet,
  setResult,
  setIsLoading,
  params = null
) => {
  let endpoint = getAPIEndpoint(action, values);
  //
  switch (action.method) {
    case "get":
      await apiGet(endpoint, {
        setIsLoading,
        options: params ? { params } : null,
        setResult: setResult,
      });
      break;
    default:
      console.error("unsupported method for API call", action.method);
      break;
  }
  return endpoint;
};

export const convertToDefEventPara = (name, value) => ({
  target: {
    name,
    value,
  },
});

export const getErstelltAmEML = (timestamp) => {
  let result = not_available;
  if (!isNaN(Date.parse(timestamp)))
    result = format(
      zonedTimeToUtc(new Date(timestamp), APP_TIMEZONE),
      "eeee, dd. MMMM yyyy 'um' HH:mm",
      { timeZone: "UTC", locale: de }
    );
  return result;
};

/**
 * Berechnet den Prozentwert von base, aber maximal bis maxValue.
 * @param {number} base - Ausgangswert
 * @param {number} percent - Prozentwert (z.B. 20 für 20%)
 * @param {number} [maxValue] - Optionaler Maximalwert
 * @returns {number}
 */
export function getPercentValueWithMax(base, percent, maxValue) {
  if (typeof base !== "number" || typeof percent !== "number") return NaN;
  let value = (base * percent) / 100;
  if (typeof maxValue === "number") {
    value = Math.min(value, maxValue);
  }
  return value;
}

export const handleDownload = (data) => {
  const { filename, filedata } = data;

  let base64Content = filedata;
  if (base64Content.startsWith("data:"))
    base64Content = base64Content.split("base64,")[1];

  const binaryString = atob(base64Content);
  const byteArray = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    byteArray[i] = binaryString.charCodeAt(i);
  }
  const blob = new Blob([byteArray]);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
};

/**
 * Normalize a single attachment reference to `{ id, uuid?, description? }`.
 *
 * Accepts:
 *   - number | numeric string  → looked up against the session UUID map
 *   - { id, ... }              → kept as-is, enriched with uuid from session if available
 *   - falsy                    → null
 */
const normalizeAttachmentRef = (ref, getUuidForServerId) => {
  if (!ref && ref !== 0) return null;
  if (typeof ref === "number" || typeof ref === "string") {
    const id = ref;
    const uuid = getUuidForServerId?.(id);
    return uuid ? { id, uuid } : { id };
  }
  if (typeof ref === "object" && ref.id != null) {
    if (ref.uuid) return ref;
    const uuid = getUuidForServerId?.(ref.id);
    return uuid ? { ...ref, uuid } : ref;
  }
  return null;
};

/**
 * Wrap every `claim_attachments` field in `values` into the canonical
 * `{ __filepicker__: "single" | "multi", attachments: ... }` shape that the
 * backend AttachmentService expects.
 *
 * On initial save after upload the field already produces this wrapped shape;
 * on re-save of an untouched field, the value is still in the backend storage
 * shape (`{ id }` or `[{ id }, ...]`). This helper normalises both into a
 * single shape so the backend only has one code path to process.
 *
 * Mutates `values` in place and returns it for convenience.
 */
export const wrapFilepickerValuesForSubmit = (
  values,
  interpreterItems,
  getUuidForServerId
) => {
  if (!values || !Array.isArray(interpreterItems)) return values;

  for (const item of interpreterItems) {
    if (item?.control !== "claim_attachments" || !item.name) continue;

    const is_multi = item.multiple !== false;
    const raw = values[item.name];

    // already wrapped → leave as-is (picker output)
    if (
      raw &&
      typeof raw === "object" &&
      !Array.isArray(raw) &&
      raw.__filepicker__
    ) {
      continue;
    }

    // empty / unset → preserve null so backend treats as "no attachments"
    if (raw == null || raw === "" || (Array.isArray(raw) && raw.length === 0)) {
      values[item.name] = null;
      continue;
    }

    if (is_multi) {
      const list = Array.isArray(raw) ? raw : [raw];
      const refs = list
        .map((r) => normalizeAttachmentRef(r, getUuidForServerId))
        .filter(Boolean);
      values[item.name] = refs.length
        ? { __filepicker__: "multi", attachments: refs }
        : null;
    } else {
      const single = Array.isArray(raw) ? raw[0] : raw;
      const ref = normalizeAttachmentRef(single, getUuidForServerId);
      values[item.name] = ref
        ? { __filepicker__: "single", attachments: ref }
        : null;
    }
  }

  return values;
};

export const getFormFillStatus = (values, formDefinition) => {
  if (values && formDefinition) {
    const interpreterItems = flattenItems(formDefinition);
    const resultValidation = evaluate(
      "isSubFormValid()",
      values,
      interpreterItems
    );
    return {
      valid: resultValidation.result,
      numValid:
        interpreterItems.length - Object.keys(resultValidation.aerrors).length,
      numInvalid: Object.keys(resultValidation.aerrors).length,
      numAll: interpreterItems.length,
    };
  }
};
